/* Leitura e escrita de uma configuração de custo: como uma `ConfigCusto` do
 * banco vira estado de edição e volta. Nenhuma conta de custo acontece aqui;
 * quem resolve regra em reais é `custoConfig` do core. */

import type {
  ChaveCustoDireto,
  ConfigCusto,
  ConfigCustoExtra,
  Faixa,
  ModoCusto,
  SubModoCusto,
  VariavelFaixa,
} from '@/dados';
import { formatarMoeda, formatarNumero, parseNum } from '@/design/formato';

/** As cinco linhas fixas de custo direto, na ordem em que a etapa as mostra.
 *  Mora aqui, e não na tabela, porque o painel de administração lê a mesma
 *  configuração e diria os mesmos cinco nomes. */
export const CAMPOS: ReadonlyArray<{ chave: ChaveCustoDireto; rotulo: string }> = [
  { chave: 'ca', rotulo: 'Material adicional CA' },
  { chave: 'inst', rotulo: 'Instalação' },
  { chave: 'proj', rotulo: 'Projeto elétrico' },
  { chave: 'homol', rotulo: 'Homologação' },
  { chave: 'art', rotulo: 'ART do projeto' },
];

export const MODOS: ReadonlyArray<{ valor: ModoCusto; rotulo: string }> = [
  { valor: 'fixo', rotulo: 'Valor fixo' },
  { valor: 'modulo', rotulo: 'R$ por módulo' },
  { valor: 'kwp', rotulo: 'R$ por kWp' },
  { valor: 'faixa', rotulo: 'Por faixa' },
];

export const SUB_MODOS: ReadonlyArray<{ valor: SubModoCusto; rotulo: string }> = [
  { valor: 'fixo', rotulo: 'Valor fixo' },
  { valor: 'modulo', rotulo: 'R$ por módulo' },
  { valor: 'kwp', rotulo: 'R$ por kWp' },
];

export const VARIAVEIS: ReadonlyArray<{ valor: VariavelFaixa; rotulo: string }> = [
  { valor: 'kwp', rotulo: 'kWp' },
  { valor: 'modulos', rotulo: 'Módulos' },
];

export type Configuracoes = {
  campos: Partial<Record<ChaveCustoDireto, ConfigCusto>>;
  extras: ConfigCustoExtra[];
};

/** O que o modal está editando. `chave` distingue linha fixa de custo extra. */
export type Edicao = {
  chave: string;
  titulo: string;
  cfg: ConfigCusto;
  configurado: boolean;
};

export type FaixaEdicao = {
  /** Identidade estável da linha: as faixas são reordenadas e não têm chave própria. */
  id: string;
  /** Limite superior. `null` é a faixa aberta ("acima de"). */
  ate: number | null;
  taxa: number | null;
};

/** Descrição da regra em uma linha, para a pessoa conferir sem abrir o modal. */
export function descreverRegra(cfg: ConfigCusto | undefined): string {
  if (!cfg?.modo) return 'Sem configuração: informe o valor em cada projeto.';
  if (cfg.modo === 'fixo') return `Valor fixo ${formatarMoeda(parseNum(cfg.taxa))}`;
  if (cfg.modo === 'modulo') return `${formatarMoeda(parseNum(cfg.taxa))} por módulo`;
  if (cfg.modo === 'kwp') return `${formatarMoeda(parseNum(cfg.taxa))} por kWp`;
  const quantas = cfg.faixas?.length ?? 0;
  if (quantas === 0) return 'Por faixa, sem faixas definidas.';
  return `Por faixa de ${cfg.faixaVariavel === 'modulos' ? 'módulos' : 'kWp'}`;
}

/** De onde sai o número do exemplo, dito em texto embaixo da prévia. */
export function descreverExemplo(
  modo: ModoCusto,
  variavel: VariavelFaixa,
  kwpRef: number | null,
  modulosRef: number | null,
): string {
  if (modo === 'fixo') return 'Vale o mesmo em qualquer projeto.';
  if (modo === 'modulo') {
    return modulosRef === null
      ? 'Informe a quantidade de módulos do exemplo.'
      : `${formatarNumero(modulosRef)} módulos no exemplo.`;
  }
  if (modo === 'kwp') {
    return kwpRef === null
      ? 'Informe a potência do exemplo.'
      : `${formatarNumero(kwpRef)} kWp no exemplo.`;
  }
  const referenciaFaixa = variavel === 'modulos' ? modulosRef : kwpRef;
  if (referenciaFaixa === null) return 'Informe a referência do exemplo acima.';
  return `${formatarNumero(referenciaFaixa)} ${variavel === 'modulos' ? 'módulos' : 'kWp'} no exemplo.`;
}

export function montarConfig({
  modo,
  taxa,
  subModo,
  variavel,
  faixas,
}: {
  modo: ModoCusto;
  taxa: number | null;
  subModo: SubModoCusto;
  variavel: VariavelFaixa;
  faixas: FaixaEdicao[];
}): ConfigCusto {
  if (modo !== 'faixa') return { modo, taxa: taxa ?? 0 };
  return {
    modo: 'faixa',
    subModo,
    faixaVariavel: variavel,
    // Limite vazio ou zerado é faixa ABERTA, e o dado limpo disso é `null`. O
    // motor já lê `ate <= 0` como aberto, mas gravar 0 fingiria um limite.
    faixas: faixas.map((f) => ({ ate: f.ate !== null && f.ate > 0 ? f.ate : null, taxa: f.taxa ?? 0 })),
  };
}

export function taxaInicial(cfg: ConfigCusto): number | null {
  if (cfg.taxa === null || cfg.taxa === undefined || cfg.taxa === '') return null;
  return parseNum(cfg.taxa);
}

export function faixasIniciais(cfg: ConfigCusto): FaixaEdicao[] {
  const salvas = cfg.modo === 'faixa' ? (cfg.faixas ?? []) : [];
  if (salvas.length === 0) return [{ id: crypto.randomUUID(), ate: null, taxa: null }];
  return ordenarFaixas(
    salvas.map((faixa: Faixa) => ({
      id: crypto.randomUUID(),
      ate: limiteDaFaixa(faixa),
      taxa: faixa.taxa === null || faixa.taxa === undefined || faixa.taxa === '' ? null : parseNum(faixa.taxa),
    })),
  );
}

function limiteDaFaixa(faixa: Faixa): number | null {
  const n = parseNum(faixa.ate);
  return n > 0 ? n : null;
}

/** Ordem crescente, com a faixa aberta sempre no fim. */
export function ordenarFaixas(lista: FaixaEdicao[]): FaixaEdicao[] {
  return lista.slice().sort((a, b) => (a.ate ?? Infinity) - (b.ate ?? Infinity));
}
